import { Handshake } from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import {
  coproductionStatusLabel,
  percentageLabel,
  useMyCoproductionsQuery,
} from '@entities/partnership'
import type { CoproductionInvite } from '@entities/partnership'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { coproductionStatusTone } from '../model/coproduction-status'
import {
  useAcceptCoproductionMutation,
  useEndCoproductionMutation,
  useRejectCoproductionMutation,
} from '../api/coproduction.mutations'
import {
  headerText,
  inviteActions,
  inviteCard,
  inviteInfo,
  inviteMeta,
  inviteTitle,
  list,
  page,
  subtitle,
  title,
} from './coproductions.css'

function isInviteExpired(invite: CoproductionInvite) {
  if (!invite.expiresAt) {
    return false
  }
  const expiry = Date.parse(invite.expiresAt)
  return Number.isFinite(expiry) && expiry <= Date.now()
}

export function CoproductionsScreen({ inviteToken }: { inviteToken?: string }) {
  const invites = useMyCoproductionsQuery()
  const accept = useAcceptCoproductionMutation()
  const reject = useRejectCoproductionMutation()
  const end = useEndCoproductionMutation()
  const orderedInvites =
    invites.data && inviteToken
      ? [...invites.data].sort((a, b) =>
          a.token === inviteToken ? -1 : b.token === inviteToken ? 1 : 0,
        )
      : (invites.data ?? [])
  const targetInviteMissing =
    invites.data !== undefined &&
    inviteToken !== undefined &&
    !invites.data.some((invite) => invite.token === inviteToken)

  function handleAccept(invite: CoproductionInvite) {
    accept.mutate(invite.token, {
      onSuccess: () => toast.success('Coprodução aceita.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível aceitar o convite',
            fallback: 'Tente novamente em alguns instantes.',
          }),
        ),
    })
  }

  function handleReject(invite: CoproductionInvite) {
    reject.mutate(invite.token, {
      onSuccess: () => toast.info('Convite recusado.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível recusar o convite',
            fallback: 'Tente novamente em alguns instantes.',
          }),
        ),
    })
  }

  function handleEnd(invite: CoproductionInvite) {
    if (
      !window.confirm(
        'Encerrar esta parceria de coprodução? A mudança vale para as próximas vendas.',
      )
    ) {
      return
    }
    end.mutate(invite.id, {
      onSuccess: () => toast.info('Parceria de coprodução encerrada.'),
      onError: (error) =>
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível encerrar a parceria',
            fallback: 'Tente novamente em alguns instantes.',
          }),
        ),
    })
  }

  return (
    <div className={page} data-testid="coproductions-screen">
      <div className={headerText}>
        <span className={title}>Produtos que coproduzo</span>
        <span className={subtitle}>
          Convites de coprodução que você recebeu e os produtos que divide com outros produtores.
        </span>
      </div>

      {invites.isPending ? (
        <div className={list}>
          {[0, 1].map((index) => (
            <Card key={index} padding="md">
              <Skeleton width="35%" height={16} />
              <div style={{ height: 8 }} />
              <Skeleton width="20%" height={12} />
            </Card>
          ))}
        </div>
      ) : invites.isError ? (
        <ErrorState
          onRetry={() => {
            void invites.refetch()
          }}
        />
      ) : orderedInvites.length === 0 ? (
        <EmptyState
          icon={<Handshake size={22} />}
          title="Nenhum convite de coprodução"
          description={
            inviteToken
              ? 'Este convite não está disponível para esta conta, já expirou ou já foi respondido.'
              : 'Quando um produtor te convidar para coproduzir, o convite aparece aqui.'
          }
        />
      ) : (
        <div className={list}>
          {targetInviteMissing ? (
            <Card padding="md">
              <span className={inviteTitle}>Convite não encontrado</span>
              <p className={inviteMeta}>
                Este convite pode ter expirado, já ter sido respondido ou pertencer a outra conta.
              </p>
            </Card>
          ) : null}
          {orderedInvites.map((invite) => {
            const pendingAccept = accept.isPending && accept.variables === invite.token
            const pendingReject = reject.isPending && reject.variables === invite.token
            const pendingEnd = end.isPending && end.variables === invite.id
            const expired = invite.status === 'PENDENTE' && isInviteExpired(invite)
            const highlighted = inviteToken === invite.token
            return (
              <Card key={invite.id} padding="md" className={inviteCard({ highlighted })}>
                <div className={inviteInfo}>
                  <span className={inviteTitle}>
                    {invite.product?.name ?? 'Convite de coprodução'}
                  </span>
                  {invite.producer ? (
                    <span className={inviteMeta}>Convidado por {invite.producer.name}</span>
                  ) : null}
                  <span className={inviteMeta}>
                    Sua parte: {percentageLabel(invite.percentage)}
                  </span>
                </div>
                {expired ? (
                  <StatusBadge tone="closed" label="Expirado" size="sm" />
                ) : invite.status === 'PENDENTE' ? (
                  <div className={inviteActions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={pendingAccept || pendingReject}
                      onClick={() => {
                        handleReject(invite)
                      }}
                    >
                      {pendingReject ? 'Recusando...' : 'Recusar'}
                    </Button>
                    <Button
                      size="sm"
                      disabled={pendingAccept || pendingReject}
                      onClick={() => {
                        handleAccept(invite)
                      }}
                    >
                      {pendingAccept ? 'Aceitando...' : 'Aceitar'}
                    </Button>
                  </div>
                ) : invite.status === 'ATIVO' ? (
                  <div className={inviteActions}>
                    <StatusBadge
                      tone={coproductionStatusTone(invite.status)}
                      label={coproductionStatusLabel(invite.status)}
                      size="sm"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={pendingEnd}
                      onClick={() => {
                        handleEnd(invite)
                      }}
                    >
                      {pendingEnd ? 'Encerrando...' : 'Encerrar parceria'}
                    </Button>
                  </div>
                ) : (
                  <StatusBadge
                    tone={coproductionStatusTone(invite.status)}
                    label={coproductionStatusLabel(invite.status)}
                    size="sm"
                  />
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
